import {
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { AuthService } from '../../../core/services/auth/auth.service';
import { FocusTrapDirective } from '../../../shared/directives/focus-trap/focus-trap.directive';
import { IconComponent } from '../../../shared/components/icon/icon.component';
import { formatDob, formatPhoneNumber } from '../../../shared/utils/date-time/date-time.utils';
import { logHttpError } from '../../../shared/utils/logging/http-error-logging.utils';
import { RequestDetail, RequestDetailPatient } from '../../models/request-detail';
import { PatientPortalUserRequestsService } from '../../services/patient-portal-user-requests.service';
import {
  getIdentifierAriaLabel,
  getIdentifierParts,
  maskSsnLastFive,
} from '../../utils/identifier.utils';
import {
  hasSubmittedMedicalRecordNumber,
  isDateOfBirthMismatch,
  isMedicalRecordNumberMismatch,
  isNameMismatch,
} from '../../utils/identity-match.utils';
import { getMatchConfidence, requiresIdentityOverride } from '../../utils/match-confidence.utils';
import { formatPatientPcp } from '../../../shared/utils/patient-display/patient-display.utils';

/**
 * Client-side cap on the override justification. The value is persisted with the
 * audit record, so an over-length body comes back as a validation 400 that this
 * modal can only report as a generic "please try again" — a dead end the staff
 * member can't act on. Enforced in the template with `maxlength` as well, so the
 * request is never sent in a state the API will reject.
 */
export const MAX_OVERRIDE_JUSTIFICATION_LENGTH = 1000;

/**
 * Stands in for a field the request arrived without, inside the banner's
 * submitted-versus-record quotes. Held here rather than inline in the template so
 * Prettier can't wrap a newline and twelve spaces into the middle of the
 * literal — harmless where HTML collapses whitespace, silently wrong the moment
 * the copy moves into an `aria-label`, a `title`, or a `white-space: pre` block.
 */
const NOT_PROVIDED = 'not provided';

interface MismatchItem {
  /** Display name shown in the policy banner. */
  field: string;
  /** Canonical field name the API expects in `mismatchedFields`. */
  apiField: 'Name' | 'DateOfBirth' | 'MedicalRecordNumber';
  submitted: string;
  matched: string;
}

@Component({
  selector: 'app-confirm-request-modal',
  imports: [IconComponent, FocusTrapDirective],
  templateUrl: './confirm-request-modal.html',
  styleUrl: './confirm-request-modal.scss',
})
export class ConfirmRequestModal implements OnInit {
  private readonly destroyRef = inject(DestroyRef);
  private readonly elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly authService = inject(AuthService);
  private readonly requestsService = inject(PatientPortalUserRequestsService);

  readonly requestId = input.required<number>();
  readonly request = input.required<RequestDetail>();
  readonly selectedPatient = input<RequestDetailPatient | null>(null);

  readonly cancelled = output<void>();
  readonly confirmed = output<void>();

  readonly isConfirming = signal(false);
  readonly confirmError = signal<string | null>(null);
  readonly mismatchAcknowledged = signal(false);
  readonly overrideJustification = signal('');

  /**
   * Flipped on blur (and on a blocked confirm) so the "required" message only
   * appears once the staff member has moved on from the field, rather than
   * scolding them before they have typed anything. Same pattern as the audit-log
   * search modals.
   */
  readonly justificationTouched = signal(false);

  readonly justificationLength = computed(() => this.overrideJustification().length);

  readonly maxJustificationLength = MAX_OVERRIDE_JUSTIFICATION_LENGTH;

  /** Inline validation message for the justification, or null when it is valid. */
  readonly justificationError = computed<string | null>(() => {
    const value = this.overrideJustification();
    if (value.trim().length === 0) {
      return 'A justification is required to override identity verification.';
    }
    if (value.length > MAX_OVERRIDE_JUSTIFICATION_LENGTH) {
      return `Shorten the justification to ${MAX_OVERRIDE_JUSTIFICATION_LENGTH} characters or fewer.`;
    }
    return null;
  });

  /** True once the field has been left and it still has an error to show. */
  readonly showJustificationError = computed(
    () => this.justificationTouched() && this.justificationError() !== null,
  );

  // Identifier precedence and masking come from identifier.utils — the single
  // source of truth shared with the request detail screen.
  private readonly portalIdentifierParts = computed(() => getIdentifierParts(this.request()));

  readonly portalIdentifierLabel = computed(() => {
    const { typeLabel, fieldLabel, hasIdentifier } = this.portalIdentifierParts();
    // This layout labels the empty case 'Identifier' rather than '—'.
    return hasIdentifier ? typeLabel : fieldLabel;
  });

  readonly portalIdentifierValue = computed(() => this.portalIdentifierParts().value);

  readonly isPortalIdentifierMasked = computed(() => this.portalIdentifierParts().isMasked);

  readonly portalIdentifierAriaLabel = computed(() => getIdentifierAriaLabel(this.request()));

  /**
   * Whether the submitted column's identifier field is the one that doesn't match.
   *
   * That field shows the MRN when one was submitted and the masked SSN otherwise, so it is checked
   * against the identifier actually on display rather than against
   * {@link medicalRecordNumberMatches} alone. The two can only diverge if the precedence in
   * `identifier.utils` changes — at which point this would quietly start flagging a masked SSN as
   * a mismatched MRN, which is the kind of wrong a staff member cannot see.
   */
  readonly isPortalIdentifierMismatch = computed(
    () => this.portalIdentifierParts().typeLabel === 'MRN' && !this.medicalRecordNumberMatches(),
  );

  // The submitted name is nullable on the API. This stays empty rather than the
  // column's dash because it feeds the mismatch predicate and the override
  // banner's "submitted" copy — a dash in either would assert a name that was
  // never submitted. The dash belongs to the cell, so the template applies it;
  // the banner names the absence in words instead (see NOT_PROVIDED).
  // (The audited `mismatchedFields` payload carries only the field names, never
  // these values — see `confirm()`.)
  readonly submittedName = computed(() => this.request().patientDisplayName ?? '');

  readonly portalDob = computed(() => formatDob(this.request().dateOfBirth));
  readonly portalPhone = computed(() => formatPhoneNumber(this.request().phoneNumber));

  readonly patientDob = computed(() => {
    const p = this.selectedPatient();
    return p ? formatDob(p.dateOfBirth) : '—';
  });

  readonly patientHasSsn = computed(() => !!this.selectedPatient()?.lastFiveSocialSecurityNumber);

  readonly patientSsnDisplay = computed(() => {
    const ssn = this.selectedPatient()?.lastFiveSocialSecurityNumber;
    return ssn ? maskSsnLastFive(ssn) : '—';
  });

  readonly patientSsnAriaLabel = computed(() => {
    const ssn = this.selectedPatient()?.lastFiveSocialSecurityNumber;
    return ssn ? `SSN ending in ${ssn}` : 'SSN not available';
  });

  readonly patientEmail = computed(() => this.selectedPatient()?.emailAddress ?? '—');
  readonly patientPhone = computed(() => formatPhoneNumber(this.selectedPatient()?.phoneNumber));

  // Shares formatPatientPcp's notion of "no provider on the record" with the
  // detail and search screens — an empty string must not render as a blank cell
  // here while it reads as absent there. The fallback is this modal's dash, to
  // match the other cells in the same column.
  readonly patientPcp = computed(() =>
    formatPatientPcp(this.selectedPatient()?.primaryCareProvider, '—'),
  );

  // Name/DOB comparison lives in identity-match.utils — the same predicates the
  // detail screen's search-result mismatch chips use, so the chip and this
  // override gate can't drift apart.
  readonly nameMatches = computed(() => {
    const patient = this.selectedPatient();
    if (!patient) return true;
    return !isNameMismatch(this.submittedName(), patient.displayName);
  });

  readonly dobMatches = computed(() => {
    const patient = this.selectedPatient();
    if (!patient) return true;
    return !isDateOfBirthMismatch(this.request().dateOfBirth, patient.dateOfBirth);
  });

  /**
   * True unless the request submitted an MRN this record fails to corroborate. A request with no
   * MRN matches here — most of them have none, and absence is nothing to override.
   */
  readonly medicalRecordNumberMatches = computed(() => {
    const patient = this.selectedPatient();
    if (!patient) return true;
    return !isMedicalRecordNumberMismatch(
      this.request().medicalRecordNumber,
      patient.medicalRecordNumber,
    );
  });

  /**
   * Whether linking needs the documented override. Derived from the same match-confidence level the
   * Search Patient Records screen shows, so a record that screen scores High can never open here as
   * a mismatch (or the reverse).
   */
  readonly hasMismatch = computed(() => {
    const patient = this.selectedPatient();
    if (!patient) return false;
    const request = this.request();
    return requiresIdentityOverride(
      getMatchConfidence(
        {
          patientDisplayName: request.patientDisplayName,
          dateOfBirth: request.dateOfBirth,
          medicalRecordNumber: request.medicalRecordNumber,
        },
        patient,
      ),
    );
  });

  /**
   * What the success banner claims it checked. It names the MRN only when one was actually
   * submitted — on a request without one there is nothing to have agreed, and a banner claiming
   * otherwise would be the screen vouching for a comparison it never made.
   */
  readonly consistentFields = computed(() =>
    hasSubmittedMedicalRecordNumber(this.request().medicalRecordNumber)
      ? 'Name, DOB and MRN are'
      : 'Name and DOB are',
  );

  readonly headerTitle = computed(() =>
    this.hasMismatch() ? 'Identity Mismatch Detected' : 'Confirm & Link Patient Record',
  );

  readonly headerSubtitle = computed(() =>
    this.hasMismatch()
      ? 'One or more fields do not match the patient record. Manual override required.'
      : 'Review carefully before confirming — this action is permanent and audit-logged.',
  );

  readonly confirmButtonLabel = computed(() => {
    if (this.isConfirming()) return 'Confirming…';
    return this.hasMismatch() ? 'Confirm Link Anyway' : 'Confirm Link';
  });

  /**
   * Itemized mismatches for the policy banner (AC7). Also the single source of
   * the `mismatchedFields` sent with the confirm body — the banner and the
   * audit payload must never disagree.
   */
  readonly mismatches = computed<MismatchItem[]>(() => {
    const patient = this.selectedPatient();
    if (!patient) return [];
    const items: MismatchItem[] = [];
    if (!this.nameMatches()) {
      items.push({
        field: 'Name',
        apiField: 'Name',
        // Names the absence rather than quoting an empty string. Still not the
        // column's dash — see `submittedName`.
        submitted: this.submittedName() || NOT_PROVIDED,
        matched: patient.displayName,
      });
    }
    if (!this.dobMatches()) {
      items.push({
        field: 'Date of Birth',
        apiField: 'DateOfBirth',
        submitted: this.portalDob(),
        matched: this.patientDob(),
      });
    }
    if (!this.medicalRecordNumberMatches()) {
      items.push({
        field: 'MRN',
        apiField: 'MedicalRecordNumber',
        // Only reached when the request *did* submit one, so this never names an absence — but the
        // record's side can be empty, and that is the discrepancy being reported.
        submitted: this.request().medicalRecordNumber ?? NOT_PROVIDED,
        matched: patient.medicalRecordNumber || NOT_PROVIDED,
      });
    }
    return items;
  });

  private readonly overrideComplete = computed(
    () => this.mismatchAcknowledged() && this.justificationError() === null,
  );

  readonly canConfirm = computed(() => {
    if (!this.selectedPatient() || this.isConfirming()) return false;
    if (this.hasMismatch() && !this.overrideComplete()) return false;
    return true;
  });

  ngOnInit(): void {
    setTimeout(() => {
      this.elementRef.nativeElement.querySelector<HTMLElement>('[role="dialog"]')?.focus();
    });
  }

  cancel(): void {
    this.cancelled.emit();
  }

  toggleMismatchAcknowledgment(checked: boolean): void {
    this.mismatchAcknowledged.set(checked);
  }

  updateOverrideJustification(value: string): void {
    this.overrideJustification.set(value);
  }

  markJustificationTouched(): void {
    this.justificationTouched.set(true);
  }

  confirm(): void {
    if (!this.canConfirm()) {
      // Reveal the outstanding justification error rather than leaving a
      // disabled button with no stated reason.
      if (this.hasMismatch()) {
        this.justificationTouched.set(true);
      }
      return;
    }
    const patient = this.selectedPatient();
    if (!patient) return;

    // Confirm-and-link is permanent and audit-logged — never attribute it to a
    // fabricated admin id. Without a signed-in user, refuse and say why.
    const userId = this.authService.currentUser()?.patientPortalAdminUserId;
    if (userId == null) {
      this.confirmError.set('Your session could not be verified. Sign in again and retry.');
      return;
    }

    this.isConfirming.set(true);
    this.confirmError.set(null);

    const mismatchedFields = this.mismatches().map((mismatch) => mismatch.apiField);

    this.requestsService
      .confirmRequest(this.requestId(), {
        patientId: patient.patientId,
        patientPortalAdminUserId: userId,
        version: this.request().version,
        ...(mismatchedFields.length > 0
          ? {
              isMismatchOverride: true,
              mismatchedFields,
              overrideJustification: this.overrideJustification().trim(),
            }
          : {}),
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isConfirming.set(false);
          this.confirmed.emit();
        },
        error: (error) => {
          logHttpError('Failed to confirm request', error);
          this.confirmError.set('Unable to confirm this request. Please try again.');
          this.isConfirming.set(false);
        },
      });
  }
}
